import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import cn from 'classnames';
import { Dialog } from 'primereact/dialog';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { displayApiErrorsOnFormFields } from '@features/campaigns/builder/utils/displayApiErrorsOnFormFields.ts';
import { FieldBrand } from '@features/campaigns/details/components/FormFields/FieldBrand/FieldBrand';
import { FieldClient } from '@features/campaigns/details/components/FormFields/FieldClient/FieldClient';
import { BillingFormFieldTypes } from '@features/campaigns/details/components/FormFields/FieldTypes.ts';
import { exportOneFlight } from '@features/campaigns/details/services/exportOneFlight.ts';
import {
  ExportOneflightFormValues,
  exportOneFlightValidationSchema,
  OneflightParams,
} from '@features/campaigns/details/shared/types/oneFlight.ts';
import { DefaultActionsFooter } from '@shared/components/Modal/Footers/DefaultActionsFooter/DefaultActionsFooter';
import { SuccessMessage } from '@shared/components/Toast/SuccessMessage/SuccessMessage.tsx';
import { i18nNameSpace } from '@shared/consts/i18n';

import styles from './ExportOneFlightModal.module.scss';

interface ExportOneFlightModalProps {
  isModalVisible: boolean;
  toggleModalVisibility: () => void;
  headerText: string;
}

export default function ExportOneFlightModal({
  isModalVisible,
  toggleModalVisibility,
  headerText,
}: ExportOneFlightModalProps) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const { campaignId } = useParams();
  const form = useForm<ExportOneflightFormValues>({
    resolver: zodResolver(exportOneFlightValidationSchema),
  });

  const clientFieldValue = useWatch({ control: form.control, name: BillingFormFieldTypes.CLIENT });
  const brandFieldValue = useWatch({ control: form.control, name: BillingFormFieldTypes.BRAND });

  const disabledSubmit = !clientFieldValue && !brandFieldValue;

  const clearBrandName = () => {
    form.setValue(BillingFormFieldTypes.BRAND, '');
  };
  const clearClientName = () => {
    form.setValue(BillingFormFieldTypes.CLIENT, '');
    clearBrandName();
  };

  const { mutate: exportOneFlightMutation, isPending: exportOneFlightLoading } = useMutation({
    mutationFn: ({ campaignId, brandId }: OneflightParams) =>
      exportOneFlight({
        campaignId,
        brandId,
      }),
    onError: (error) => {
      displayApiErrorsOnFormFields(form.setError, error);
    },
    onSuccess: async () => {
      toast.success(
        <SuccessMessage message={t('api.success.campaign.campaignExportedToOneflight', { ns: i18nNameSpace.API })} />,
      );
      toggleModalVisibility();
      clearClientName();
    },
  });

  const submitCallback = () => {
    if (campaignId && brandFieldValue) {
      exportOneFlightMutation({ campaignId: campaignId, brandId: brandFieldValue });
    }
  };

  const closeModal = () => {
    clearClientName();
    toggleModalVisibility();
  };

  return (
    <Dialog
      className={cn(styles.wrapper, { [styles.noDisplay]: !isModalVisible })}
      visible={true}
      onHide={closeModal}
      showHeader
      header={headerText}
      draggable={false}
      resizable={false}
      modal={isModalVisible}
      footer={
        <DefaultActionsFooter
          submitLabel={t('builder.modals.yesSaveOneFlight')}
          cancelLabel={t('builder.modals.cancel')}
          cancelCallback={closeModal}
          submitCallback={submitCallback}
          submitCallbackActionIsLoading={exportOneFlightLoading}
          submitCallbackActionIsDisabled={disabledSubmit}
        />
      }
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(submitCallback)} autoComplete={'off'}>
          <div className={styles.formWrapper}>
            <div className='grid'>
              <div className='row'>
                <div className='col-12'>
                  <FieldClient
                    restoredInputValue={clientFieldValue}
                    clearSelectedValuesHandler={clearClientName}
                    disabled={false}
                    showCode={false}
                  />
                </div>
              </div>
              <div className='row'>
                <div className='col-12'>
                  <FieldBrand
                    clientFieldValue={clientFieldValue}
                    clearSelectedValuesHandler={clearBrandName}
                    wasFormRestored={true}
                    disabled={!clientFieldValue}
                    showCode={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
}
